/**
 * Estado em memória para demo (lista + lidas).
 * Formato bruto alinhado ao que `NotificationService` mapeia para o Redux.
 *
 * Integra com `demoScenario.js` para que reset/roteiro inicial fiquem centralizados
 * (ver `src/demo/demoScenario.js`).
 */

import {
  DEMO_ACK_COMMUNICATION_ID,
  DEMO_ACK_NOTIFICATION_ID,
  DEMO_ACK_SUBSCRIBER_ID,
  DEMO_SCENARIO
} from "./demoScenario";
import { DEMO_ACK_SESSION_KEY } from "./resetDemo";

function isoHoursAgo(h) {
  const d = new Date();
  d.setHours(d.getHours() - h);
  return d.toISOString();
}

/** Lê do sessionStorage se a ciência obrigatória já foi confirmada nesta sessão demo. */
function hasAckBeenConfirmed() {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(DEMO_ACK_SESSION_KEY) === "1";
}

const rows = [
  {
    id: 1,
    model: "Communication",
    modelId: 1,
    message: "Reunião de pais — turmas do ensino fundamental",
    created_at: isoHoursAgo(2),
    subscriber: { id: 501, opened: false }
  },
  {
    id: 2,
    model: "Class",
    modelId: 100,
    message: "Lembrete: entrega de trabalho de Ciências até sexta",
    created_at: isoHoursAgo(8),
    subscriber: { id: 502, opened: false }
  },
  {
    id: 3,
    model: "Communication",
    modelId: 2,
    message: "Circular: calendário de provas bimestrais (informe)",
    created_at: isoHoursAgo(26),
    subscriber: { id: 503, opened: false }
  },
  {
    id: 4,
    model: "Support",
    modelId: 0,
    message: "Seu chamado #892 foi atualizado",
    created_at: isoHoursAgo(48),
    subscriber: { id: 504, opened: true }
  },
  {
    id: 5,
    model: "Class",
    modelId: 101,
    message: "Aula de campo — confirmação de presença",
    created_at: isoHoursAgo(72),
    subscriber: { id: 505, opened: true }
  }
];

function cloneRow(r) {
  return {
    ...r,
    subscriber: { ...r.subscriber }
  };
}

/**
 * Notificação dinâmica de ciência obrigatória no sino, sincronizada com o AckDialog.
 * - Some quando o professor confirma a ciência (sessionStorage `DEMO_ACK_SESSION_KEY === "1"`).
 * - Volta ao cenário inicial quando o demo é resetado (ver `resetDemo.js`).
 * - `action.openCommunicationId` serve para o sino encaminhar direto ao detalhe.
 */
function buildAckNotification() {
  if (!DEMO_SCENARIO.showAckNotification) return null;
  if (hasAckBeenConfirmed()) return null;

  return {
    id: DEMO_ACK_NOTIFICATION_ID,
    model: "Communication",
    modelId: DEMO_ACK_COMMUNICATION_ID,
    message:
      "Ciência obrigatória: Normas de avaliação 2026. Abrir e confirmar leitura.",
    created_at: isoHoursAgo(1),
    subscriber: { id: DEMO_ACK_SUBSCRIBER_ID, opened: false },
    ackRequired: true,
    action: { openCommunicationId: DEMO_ACK_COMMUNICATION_ID }
  };
}

/** Acrescenta as notificações dinâmicas (prioridade) no topo da lista. */
function withDynamicNotifications(list) {
  const ack = buildAckNotification();
  return ack ? [ack, ...list] : list;
}

/** Lista para GET `Notifications` (paginação simples). */
export function getDemoNotificationsPage(page) {
  const pageSize = 10;
  const p = Number(page) || 0;
  const base = rows.map(cloneRow);
  const full = p === 0 ? withDynamicNotifications(base) : base;
  const start = p * pageSize;
  return full.slice(start, start + pageSize);
}

/** Total de itens (histórico). */
export function getDemoNotificationCount() {
  return withDynamicNotifications(rows).length;
}

/** Não lidas — badge no menu + `WaybeeUsers/mySessionInfo`. */
export function getDemoNotificationsUnreadCount() {
  const base = rows.filter(r => !r.subscriber.opened).length;
  const ack = buildAckNotification();
  return ack && !ack.subscriber.opened ? base + 1 : base;
}

/**
 * Resposta esperada por `utils/markAsRead` após PUT .../read.
 * @returns {{ notifications: { id: number }[], communications: [] }}
 */
export function markDemoNotificationRead(notificationId) {
  const id = Number(notificationId);

  if (id === DEMO_ACK_NOTIFICATION_ID) {
    // Ciência obrigatória só é "lida" quando o professor confirma no AckDialog.
    // Ignoramos o mark-as-read vindo do sino para manter o destaque até o ack.
    return { notifications: [], communications: [] };
  }

  const row = rows.find(r => r.id === id);
  if (!row || row.subscriber.opened) {
    return { notifications: [], communications: [] };
  }
  row.subscriber.opened = true;
  return {
    notifications: [{ id: row.subscriber.id }],
    communications: []
  };
}
